/**
 * Fix 17A — first-touch attribution capture (Cookie-only identity).
 *
 * Fires a fire-and-forget `POST /api/attribution/first-touch` on every
 * qualifying landing route.
 *
 * Consent contract:
 *   • First-party operational data used only for order attribution
 *     against verified Stripe purchases.
 *   • Does NOT depend on analytics/advertising consent and does NOT
 *     load or fire any third-party tracker.  Consent still gates
 *     `logPageview()`, GA4, Meta Pixel, TikTok Pixel, Snapchat Pixel,
 *     and Google Ads tags — those code paths are untouched.
 *
 * Identity contract:
 *   • The `djs_did` cookie is managed server-side by the FastAPI
 *     middleware `djs_did_cookie` (see server.py). The cookie is
 *     HttpOnly + Secure + SameSite=Lax. JavaScript has NO access to
 *     it and cannot forward it as a header or body field.
 *   • Because the middleware runs on every `/api/*` request, the very
 *     first attribution beacon carries a valid device_hash — either
 *     from an existing HttpOnly cookie the browser attaches
 *     automatically on this same-origin `fetch(credentials:"same-
 *     origin")` request, or from a fresh UUID the middleware generates
 *     for THIS request and returns as Set-Cookie in the response.
 *   • This module never reads or writes any cookie.
 *   • If cookies are blocked entirely, the middleware skips the write
 *     and the server responds
 *     `{captured:false, reason:"no_device_id"}` — preserving the
 *     existing "cookies-blocked is neutral" contract.
 */

const API_BASE = `${process.env.REACT_APP_BACKEND_URL || ""}/api`;
const SESSION_FLAG_KEY = "djs_ft_sent";
const _ATTR_KEYS = [
  "gclid", "gbraid", "wbraid",
  "utm_source", "utm_medium", "utm_campaign",
  "utm_id", "utm_term", "utm_content",
];

function _pick(url) {
  const out = {};
  try {
    const q = new URLSearchParams(url.search || "");
    _ATTR_KEYS.forEach((k) => {
      const v = q.get(k);
      if (v != null) {
        const s = String(v).trim();
        if (s) out[k] = s;
      }
    });
  } catch { /* noop */ }
  return out;
}

function _hasAnyAttribution(picked) {
  return Object.keys(picked).length > 0;
}

function _hasLocalSessionFlag() {
  try {
    return typeof sessionStorage !== "undefined" &&
      sessionStorage.getItem(SESSION_FLAG_KEY) === "1";
  } catch { return false; }
}

function _markLocalSessionFlag() {
  try {
    if (typeof sessionStorage !== "undefined") {
      sessionStorage.setItem(SESSION_FLAG_KEY, "1");
    }
  } catch { /* noop */ }
}

/**
 * Sends the first-touch payload. Best-effort, never throws.
 *
 * The `djs_did` HttpOnly cookie is managed by the backend middleware.
 * The browser attaches it automatically on this same-origin request.
 * This module has zero cookie access.
 */
export function captureFirstTouch() {
  try {
    if (typeof window === "undefined") return;
    const loc = window.location || {};
    const picked = _pick(loc);
    const hasParams = _hasAnyAttribution(picked);

    if (!hasParams && _hasLocalSessionFlag()) return;

    const body = {
      landing_url: loc.href || null,
      referrer: (typeof document !== "undefined" ? document.referrer : "") || null,
      ...picked,
    };
    const endpoint = `${API_BASE}/attribution/first-touch`;
    const payload = JSON.stringify(body);

    // `credentials: "same-origin"` ensures the browser attaches the
    // HttpOnly `djs_did` cookie automatically. No JS-visible identifier
    // is included anywhere in the request body or headers.
    fetch(endpoint, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: payload,
      keepalive: true,
      credentials: "same-origin",
    })
      .then(() => _markLocalSessionFlag())
      .catch(() => { /* best-effort */ });
  } catch { /* noop */ }
}
